import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { StatsStore } from "../../../assets/store/stats.store";
import HeroVideo from "../../../assets/video/hero.mp4";
import PrimaryButton from "../../../common/components/button/PrimaryButton";
import RotatingEyebrow from "../../../common/components/eyebrow/RotatingEyebrow";
import { HeadingBlock } from "../../../common/components/layout/HeadingBlock";
import CountUpStat from "../../../common/components/stats/CountUpStat";
import Description from "../../../common/components/text/Description";
import { Theme } from "../../../common/themes/theme";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Container = styled.div`
  position: relative;
  width: 100%;
  height: fit-content;
  min-height: 620px;
  display: flex;
  align-items: center;
  overflow: hidden;
  font-family: ${Theme.font.fontFamily.sans};

  @media (max-width: ${Theme.breakpoints.TABLET_LARGE}px) {
    min-height: 100svh;
    align-items: flex-end;
  }
`;

const HeroBg = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HeroTint = styled.div`
  position: absolute;
  inset: 0;
  height: 100%;
  background: linear-gradient(
    105deg,
    ${Theme.color.background.light} 0%,
    rgba(255, 255, 255, 0.5) 40%,
    rgba(79, 79, 79, 0.1) 55%,
    transparent 100%
  );
  z-index: 1;

  @media (max-width: ${Theme.breakpoints.TABLET_LARGE}px) {
    background: linear-gradient(
      180deg,
      rgba(248, 247, 245, 0.1) 0%,
      ${Theme.color.background.light} 80%,
      ${Theme.color.background.light} 100%
    );
  }
`;

const RedBar = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  background: ${Theme.color.text.primary};
  z-index: 4;
`;

const Content = styled.div`
  position: relative;
  z-index: 2;
  padding: 50px 40px;
  max-width: 580px;

  p {
    max-width: 480px;
  }

  @media (max-width: ${Theme.breakpoints.TABLET_LARGE}px) {
    padding: 36px 24px 32px;
    max-width: 100%;

    p {
      max-width: 100%;
    }
  }
`;

const Heading = styled.h1`
  letter-spacing: -0.01em;
  text-transform: uppercase;
  font-size: clamp(38px, 5vw, 56px);
  color: ${Theme.color.text.heading};
  font-weight: ${Theme.font.fontWeight.bold};
  font-family: ${Theme.font.fontFamily.heading};
  line-height: ${Theme.font.lineHeight.tight};
  margin: 0;

  em {
    color: ${Theme.color.text.primary};
    font-style: normal;
  }

  @media (max-width: ${Theme.breakpoints.TABLET_LARGE}px) {
    font-size: clamp(36px, 10vw, 48px);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 24px;

  @media (max-width: ${Theme.breakpoints.TABLET_LARGE}px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    margin-top: 20px;

    button,
    a {
      width: 100%;
      justify-content: center;
    }
  }
`;

const BadgeColumn = styled.div`
  position: absolute;
  right: 64px;
  top: 50%;
  transform: translateY(-60%);
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: ${Theme.breakpoints.TABLET_LARGE}px) {
    display: none;
  }
`;

const Badge = styled.div`
  background: ${Theme.color.background.light};
  border: 1px solid ${Theme.color.border.light};
  border-left: 2px solid ${Theme.color.text.primary};
  border-right: 2px solid ${Theme.color.text.primary};
  padding: 10px;
  min-width: 140px;
  border-radius: ${Theme.border.borderRadius.md};
`;

const BadgeLabel = styled.p`
  margin: 0;
  text-transform: uppercase;
  font-size: ${Theme.font.fontSize.xs};
  color: ${Theme.color.text.body};
  letter-spacing: ${Theme.font.letterSpacing.widest};
`;

const BadgeValue = styled.p`
  margin: 0;
  color: ${Theme.color.text.heading};
  font-size: ${Theme.font.fontSize.base};
  font-weight: ${Theme.font.fontWeight.medium};
`;

const StatsBar = styled.div`
  display: grid;
  position: relative;
  grid-template-columns: repeat(4, 1fr);
  background: ${Theme.color.background.light};
  border-bottom: 1px solid ${Theme.color.border.light};

  @media (max-width: ${Theme.breakpoints.TABLET_SMALL}px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatItem = styled.div`
  padding: 20px 40px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  &:last-child {
    border-right: none;
  }

  @media (max-width: ${Theme.breakpoints.TABLET_SMALL}px) {
    padding: 14px 20px;
  }
`;

const badges = [
  { label: "Tolerance", value: "±0.001 mm" },
  { label: "Lead Time", value: "3–5 Days" },
  { label: "Materials", value: "30+ Alloys" },
];

function HeroSection() {
  const navigate = useNavigate();
  return (
    <Wrapper>
      <Container>
        <HeroBg autoPlay muted loop playsInline>
          <source src={HeroVideo} type="video/mp4" />
        </HeroBg>
        <HeroTint />
        <RedBar />

        <Content>
          <HeadingBlock>
            <RotatingEyebrow
              items={[
                "Instant quotes for 1-1,000 parts",
                "Lead times from 3 business days",
                "100+ metals & plastics, 50+ surface finishes",
              ]}
            />
            <Heading>
              Precision
              <br />
              Engineered
              <br />
              <em>For The</em>
              <br />
              Future
            </Heading>
            <Description>
              Advanced CNC machining and precision metal components. Tolerances
              down to 0.001mm. Trusted by 500+ clients across aerospace,
              automotive, and medical industries.
            </Description>
          </HeadingBlock>

          <ButtonRow>
            <PrimaryButton
              onClick={() => {
                navigate("/contact");
              }}
            >
              Get a Quote
            </PrimaryButton>
            <PrimaryButton
              onClick={() => {
                navigate("/contact");
              }}
              variant="outline"
            >
              Contact Us
            </PrimaryButton>
          </ButtonRow>
        </Content>

        <BadgeColumn>
          {badges.map((b) => (
            <Badge key={b.label}>
              <BadgeLabel>{b.label}</BadgeLabel>
              <BadgeValue>{b.value}</BadgeValue>
            </Badge>
          ))}
        </BadgeColumn>
      </Container>

      <StatsBar>
        <RedBar />
        {StatsStore.map((s) => (
          <StatItem key={s.label}>
            <CountUpStat
              value={s.num}
              suffix={s.suffix}
              label={s.label}
              description={s.desc}
              duration={1200}
            />
          </StatItem>
        ))}
      </StatsBar>
    </Wrapper>
  );
}

export default HeroSection;
